import React, { Fragment } from 'react'
import moment from 'moment'
import { Button, Div, Span } from '@driftt/tide-core'
import numeral from 'numeral'
import { get } from 'lodash'
import { userHasContactsPageAccess } from 'utils/access'
import { pathOr } from 'ramda'

import Checkmark from './PeopleTab/CheckmarkIcon'
import EmptyUser from './PeopleTab/EmptyUser'
import EmptyCompany from './PeopleTab/EmptyCompany'
import { roundPercentValue } from './utils'

const hideUnenrollStates = ['Completed', 'Unenrolled', 'Unenrolled_replied', 'Unenrolled_manually', 'Unenrolled_exit_segment', 'Unenrolled_in_exclusion_segment', 'Unenrolled_sales_engaged', 'Unenrolled_account_replied', 'Unenrolled_playbook_disabled']

const failedGetMessageFactory = (metric) => <Span className="playbook-report-table-cell-failed">Could not get {metric} count for this user.</Span>

const ClickCells = {
  SENT: ({ metrics }) => {
    const sent = metrics.SENT.value
    return (
      <Fragment>
        {sent}
      </Fragment>
    )
  },
  DELIVERED: ({ metrics }) => {
    const delivered = metrics.DELIVERED.value
    const deliveredPercentage = roundPercentValue(delivered, metrics.SENT.value)
    return (
      <Fragment>
        {delivered}
        <Span className="metric-percentage-callout">
          {`${ deliveredPercentage }%`}
        </Span>
      </Fragment>
    )
  },
  OPENED: ({ metrics }, reportType) => {
    const opened = metrics.OPENED.value
    const comparisonMetrics = reportType === 'OUTBOUND_SEQUENCE' ? metrics.SENT.value : metrics.DELIVERED.value
    const openedPercentage = roundPercentValue(opened, comparisonMetrics)
    return (
      <Fragment>
        {opened}
        <Span className="metric-percentage-callout">
          {`${openedPercentage}%`}
        </Span>
      </Fragment>
    )
  },
  CLICKED: ({ metrics }, reportType) => {
    const clicked = metrics.CLICKED.value
    const comparisonMetrics = reportType === 'OUTBOUND_SEQUENCE' ? metrics.SENT.value : metrics.DELIVERED.value
    const clickPercentage = roundPercentValue(clicked, comparisonMetrics)
    return (
      <Fragment>
        {clicked}
        <Span className="metric-percentage-callout">
          {`${clickPercentage}%`}
        </Span>
      </Fragment>
    )
  }
}

const SignatureClickCells = {
  OPENED: metrics => {
    const opened = get(metrics, 'OPENED.value', failedGetMessageFactory('opened'))
    return (
      <Fragment>
        {opened}
      </Fragment>
    )
  },
  CLICKED: metrics => {
    const clicked = get(metrics, 'CLICKED.value', failedGetMessageFactory('clicked'))
    const noMetric = React.isValidElement(clicked)
    const opened = get(metrics, 'OPENED.value')
    const clickPercentage = roundPercentValue(clicked, opened)
    return (
      <Fragment>
        {clicked}
        <Span className="metric-percentage-callout">
          {!noMetric && `${clickPercentage}%`}
        </Span>
      </Fragment>
    )
  },
  CONVERSATION_INFLUENCED: metrics => {
    const conversation = get(metrics, 'CONVERSATION_INFLUENCED.value', failedGetMessageFactory('conversation'))
    const noMetric = React.isValidElement(conversation)
    const opened = get(metrics, 'OPENED.value')
    const conversationPercentage = roundPercentValue(conversation, opened)
    return (
      <Fragment>
        {conversation}
        <Span className="metric-percentage-callout">
          {!noMetric && `${conversationPercentage}%`}
        </Span>
      </Fragment>
    )
  },
  MEETING_INFLUENCED: metrics => {
    const meeting = get(metrics, 'MEETING_INFLUENCED.value', failedGetMessageFactory('meeting'))
    const noMetric = React.isValidElement(meeting)
    const opened = get(metrics, 'OPENED.value')
    const meetingPercentage = roundPercentValue(meeting, opened)
    return (
      <Fragment>
        {meeting}
        <Span className="metric-percentage-callout">
          {!noMetric && `${meetingPercentage}%`}
        </Span>
      </Fragment>
    )
  },

}

const LostCells = {
  BOUNCED: ({ metrics }) => {
    const bounced = metrics.BOUNCED.value
    const bouncedPercentage = roundPercentValue(metrics.BOUNCED.value, metrics.SENT.value)

    return (
      <Fragment>
        {bounced}
        <Span className="metric-percentage-callout">
          {`${bouncedPercentage}%`}
        </Span>
      </Fragment>
    )
  },
  UNSUBSCRIBED: ({ metrics }) => {
    const unsubscribed = metrics.UNSUBSCRIBED.value
    const unsubscribedPercentage = roundPercentValue(unsubscribed, metrics.DELIVERED.value)

    return (
      <Fragment>
        {unsubscribed}
        <Span className="metric-percentage-callout">
          {`${unsubscribedPercentage}%`}
        </Span>
      </Fragment>
    )
  }
}

const EnrollmentCells = {
  UNENROLLED: ({ metrics }) => {
    const unenrolled = metrics.BOUNCED.value + metrics.UNSUBSCRIBED.value
    const unenrolledPercent = roundPercentValue(unenrolled, metrics.DELIVERED.value)
    return (
      <Fragment>
        {unenrolled}
        <Span className="metric-percentage-callout">
          {`${unenrolledPercent}%`}
        </Span>
      </Fragment>
    )
  }
}

const CampaignEnrollmentCells = {
  UNENROLLED: ({ metrics }) => {
    const unenrolled = metrics.UNENROLLED.value
    const unenrolledPercent = roundPercentValue(unenrolled, metrics.SENT.value)
    return (
      <Fragment>
        {unenrolled}
        <Span className="metric-percentage-callout">
          {`${unenrolledPercent}%`}
        </Span>
      </Fragment>
    )
  }
}

const NumberCell = (index) => (
  <Div className="email-reports-table-number">
    {index}
  </Div>
)

const ReportCheckCell = (indicatorClass, value) => {
  const classes = `playbooks-report-indicatior-icon ${indicatorClass}`
  return (
    <Fragment>
      {value ?
        <Div>
          <Checkmark className={classes}/>
        </Div> :
        <Div className="null-cell">
          {`\u2014`}
        </Div>
      }
    </Fragment>
  )
}

const UserNameCell = ({ user, endUsers, unenroll, playbook }) => {
  const actualPerson = endUsers.find((endUser) => user.endUserId === endUser.id)
  const displayName = actualPerson ? pathOr(actualPerson.email, ['name'], actualPerson): ''
  return (
    <Div className="report-user-cell">
      <Div>
        {(actualPerson && actualPerson.avatarUrl) ?
          <img src={actualPerson.avatarUrl} alt={`${actualPerson.name}`}/>
          :
          <EmptyUser />
        }
      </Div>
      <Div>
        {actualPerson && (
          userHasContactsPageAccess() ? <a href={`/contacts/details/${actualPerson.id}`}>{actualPerson.name ? actualPerson.name : actualPerson.email}</a> :
          <p>{displayName}</p>
        )}
      </Div>
      {!hideUnenrollStates.includes(user.status) &&
        <Button onClick={() => unenroll({email: actualPerson.email, playbookId: playbook.id, name: displayName})} type="tertiary">
          Unenroll
        </Button>
      }
    </Div>
  )
}

const AgentCell = ({agents, person, personAttr}) => {
  const currentAgent = (agents) ? agents.find(agent => agent.id === person[personAttr]) : null
  return (
    <Div>
      {currentAgent &&
        <Div className="report-booked-meeting">
          {!!currentAgent.avatarUrl && <img src={currentAgent.avatarUrl} alt={currentAgent.name} />}
          <Span>{currentAgent.name}</Span>
        </Div>
      }
      {!currentAgent && `\u2014`}
    </Div>
  )
}

const CompanyCell = (company) => {
  return (
    <Fragment>
      {company ?
        <Div className="report-company-cell">
          <Div className="report-company-cell-icon">
            <EmptyCompany />
          </Div>
          <Div>
            {company}
          </Div>
        </Div> :
        ''
      }
    </Fragment>
  )
}
const StatusCell = ({ status }) => {
  const reasons = {
    'Unenrolled_manually': 'Unenrolled (Manually)',
    'Unenrolled_exit_segment': 'Unenrolled (Exited Segment)',
    'Unenrolled_in_exclusion_segment': 'Unenrolled (Matched Exclusion)',
    'Unenrolled_sales_engaged': 'Unenrolled (Sales Engaged Contact)',
    'Unenrolled': 'Unenrolled',
    'Unenrolled_replied': 'Unenrolled (Replied)',
    'Unenrolled_account_replied': 'Unenrolled (Account Reply)',
    'Unenrolled_playbook_disabled': 'Unenrolled (Playbook Disabled)',
  }

  const currentStatus = (!status.includes('Unenrolled')) ? status : reasons[status]

  return (
    <Div>
      {currentStatus}
    </Div>
  )
}

const RowFormatter =  {
  ClickRows: (e, name, index) => ({data: [
    {content: NumberCell(index + 1)},
    {content: name},
    {content: ClickCells.SENT(e)},
    {content: ClickCells.DELIVERED(e)},
    {content: ClickCells.OPENED(e)},
    {content: ClickCells.CLICKED(e)}
  ]}),
  SignatureClickRows: ({ user, metrics }, index) => ({data: [
    {content: user.name},
    {content: SignatureClickCells.OPENED(metrics)},
    {content: SignatureClickCells.CLICKED(metrics)},
    {content: SignatureClickCells.CONVERSATION_INFLUENCED(metrics)},
  ]}),
  SignatureConversationRows: ({ user, metrics }) => ({data: [
    {content: user.name},
    {content: SignatureClickCells.CONVERSATION_INFLUENCED(metrics)},
    {content: SignatureClickCells.MEETING_INFLUENCED(metrics)},
  ]}),
  ClickRowsOutboundSequence: (e, name, index, reportType) => ({data: [
    {content: NumberCell(index + 1)},
    {content: name},
    {content: ClickCells.SENT(e)},
    {content: ClickCells.OPENED(e, reportType)},
    {content: ClickCells.CLICKED(e, reportType)}
  ]}),
  LostRows: (e, name, index) => ({data: [
    {content: NumberCell(index + 1)},
    {content: name},
    {content: LostCells.BOUNCED(e)},
    {content: LostCells.UNSUBSCRIBED(e)}
  ]}),
  EnrollmentRows: (e, name, index, totals) => ({data: [
    {content: NumberCell(index + 1)},
    {content: name},
    {content: EnrollmentCells.UNENROLLED(e)}
  ]}),
  CampaignEnrollmentRows: (e, name, index) => ({data: [
    {content: NumberCell(index + 1)},
    {content: name},
    {content: CampaignEnrollmentCells.UNENROLLED(e)}
  ]}),
  ConversationRows: (e, name, index, totals) => ({data: [
    {content: (index + 1)},
    {content: name},
    {content: e.metrics.REPLIED.value},
    {content: get(e, ['metrics', 'CONVERSATION_INFLUENCED', 'value'])},
    {content: get(e, ['metrics', 'MEETING_INFLUENCED', 'value'])},   
  ]}),
  CampaignConversationRows: (e, name, index, totals) => ({data: [
    {content: (index + 1)},
    {content: name},
    {content: e.metrics.REPLIED.value},
    {content: get(e, ['metrics', 'CONVERSATION_INFLUENCED', 'value'])},
  ]}),
  DollarRows: (e, name, index, totals) => ({data: [
    {content: NumberCell(index + 1)},
    {content: name},
    {content: e.metrics.OPPORTUNITIES.value},
    {content: numeral(e.metrics.PIPELINE.value).format('($0a)')},
    {content: numeral(e.metrics.CLOSED_WON.value).format('($0a)')}
  ]}),
  PeopleRows: (person, endUsers, agents, playbook, unenroll) => ({ data: [
    {content: <UserNameCell user={person} endUsers={endUsers} playbook={playbook} unenroll={unenroll}/>},
    {content: CompanyCell(person.companyName)},
    {content: <StatusCell status={person.status} />},
    {content: moment(person.enrolledAt).format('ll')},
    {content: ReportCheckCell('playbooks-report-indicator-opened', person.opened)},
    {content: ReportCheckCell('playbooks-report-indicator-clicked', person.clicked)},
    {content: ReportCheckCell('playbooks-report-indicator-chat-influence', person.replied)},
    {content: ReportCheckCell('playbooks-report-indicator-chat-influence', person.influencedChat)},
    {content: ReportCheckCell('playbooks-report-indicator-meeting-influence', person.influencedBookedMeeting)},
    {content: <AgentCell agents={agents} person={person} personAttr={'influencedBookedMeetingAgentId'} />},
    {content: <AgentCell agents={agents} person={person} personAttr={'sentFrom'} />},
    {content: person.lastSendAt ? moment(person.lastSendAt).fromNow() : ''},
    {content: person.nextSendAt ? moment(person.nextSendAt).fromNow() : ''}
  ]})
}

export default RowFormatter
